import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { serverUrl } from '@/authService';
import { toast } from "sonner";

// Define an interface for the expected error response structure
interface ErrorResponse {
    error: string;
}

const SetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
        } else {
            toast.error("Invalid or missing password setup link.");
            navigate('/login');
        }
    }, [location.search, navigate]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!token) {
            toast.error("Password setup token is missing. Please use the link from your email.");
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        try {
            setLoading(true);

            await axios.post(
                `${serverUrl}/set-password`,
                { password, token },
                { withCredentials: true }
            );

            setSuccess(true);
            toast.success("Password set successfully! Redirecting...");

            setTimeout(() => {
                navigate('/login');
            }, 2000);

        } catch (err) {
            console.error('Error setting password:', err);
            if (axios.isAxiosError<ErrorResponse>(err) && err.response?.data?.error) {
                toast.error(err.response.data.error);
            } else {
                toast.error('Failed to set password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen flex justify-center items-center bg-gray-100">
            <div className="p-8 bg-white rounded-lg shadow-md w-full max-w-md text-center">
                <h2 className="text-2xl font-semibold mb-4">Set Your Password</h2>
                <p className="mb-6 text-gray-600">Welcome to AnswerThis Pro! Please set a password to secure your account.</p>

                {success ? (
                    <div className="text-green-600">
                        <p>Password set successfully! Redirecting you to the login page...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-left mb-1 text-sm font-medium text-gray-700">Password</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-left mb-1 text-sm font-medium text-gray-700">Confirm Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md shadow-sm disabled:opacity-50"
                            disabled={loading || !token}
                        >
                            {loading ? 'Setting Password...' : 'Set Password'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SetPassword;